import { Alert, Backdrop, Badge, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, Grid, IconButton, InputLabel, Menu, MenuItem, Select, Snackbar, Stack, Tooltip, } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import IActividad from "../../../types/Actividad";
import * as constantes from "../../../common/Constantes";
import { useNavigate } from "react-router-dom";
import ILog from "../../../types/Log";
import LogService from "../../../services/LogService";
import IAlert from "../../../types/Alert";
import ClearIcon from "@mui/icons-material/Clear";
import IMasterClass from "../../../types/MasterClass";
import IPaciente from "../../../types/Paciente";
import { ISimpleUser } from "../../config/Empleados";
import FacturacionService from "../../../services/FacturacionService";
import EuroIcon from "@mui/icons-material/Euro";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useMediaQuery, useTheme } from "@mui/material";
import FacturaClaseForm from "./FacturaClaseForm";
import IServiceClassInvoice from "../../../types/ServiceClassInvoice";
import { IFacturaCompleta } from "../facturarCita/DetalleFacturaComplementaria";
import DetalleFacturaClase from "./DetalleFacturaClase";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BlockIcon from '@mui/icons-material/Block';
import ConfigUtils from "../../../utils/Config";
import moment from "moment";
import SelectorEntidadFacturacion, { esFacturacionMultiEntidad } from "../common/SelectorEntidadFacturacion";
import { EstadoFacturacion, getEstadoFacturacion } from "../common/EstadoFacturacionIcon";

export type EstadoFacturacionClase =
  | "SIN_FACTURA"
  | "PENDIENTE_PAGO"
  | "PAGADA"
  | "EMITIDA"
  | "EMITIDA_PENDIENTE_PAGO"
  | "NO_FACTURABLE"
  | "NO_FACTURABLE_PUNTUAL"
  | "SIN_CONFIGURACION"
  | "ANULADA";

interface EstadoFacturacionItem {
  estado: EstadoFacturacionClase;
  factura?: IFacturaCompleta;
  configuracion: IServiceClassInvoice;
  motivo?: string;
}

type EstadoFacturacionResponse = Record<string, EstadoFacturacionItem>;

interface FacturacionMesProps {
  open: boolean;
  onClose: () => void;
  actividades: IActividad[];
  masterClasses: IMasterClass[];
  pacientes: IPaciente[];
  sanitarios: ISimpleUser[];
}

const estadoColorIcon: Record<EstadoFacturacionClase, "inherit" | "error" | "success" | "warning"> = {
  SIN_CONFIGURACION: "inherit",
  SIN_FACTURA: "inherit",
  NO_FACTURABLE: "warning",
  NO_FACTURABLE_PUNTUAL: "warning",
  PENDIENTE_PAGO: "error",
  PAGADA: "success",
  EMITIDA: "success",
  EMITIDA_PENDIENTE_PAGO: "error",
  ANULADA: "inherit",
};

const estadoTooltip: Record<EstadoFacturacionClase, string> = {
  SIN_CONFIGURACION: "Falta configuración de facturación",
  SIN_FACTURA: "Generar factura",
  NO_FACTURABLE: "Paciente configurado como no facturable",
  NO_FACTURABLE_PUNTUAL: "Mes configurado como no facturable",
  PENDIENTE_PAGO: "Factura pendiente de pago",
  PAGADA: "Factura pagada, pendiente de emitir",
  EMITIDA: "Factura emitida",
  EMITIDA_PENDIENTE_PAGO: "Factura emitida, pendiente de cobro",
  ANULADA: "Factura anulada",
};

const estadoPrioridad: Record<EstadoFacturacionClase, number> = {
  PENDIENTE_PAGO: 1,
  EMITIDA_PENDIENTE_PAGO: 2,
  SIN_FACTURA: 3,
  SIN_CONFIGURACION: 4,
  PAGADA: 5,
  EMITIDA: 6,
  ANULADA: 7,
  NO_FACTURABLE_PUNTUAL: 8,
  NO_FACTURABLE: 9,
};

const getEstadoFacturacionClase = (estado?: string, estadoPago?: string): EstadoFacturacionClase => {
  switch (getEstadoFacturacion(estado, estadoPago)) {
    case EstadoFacturacion.ANULADA: return "ANULADA";
    case EstadoFacturacion.EMITIDA: return "EMITIDA";
    case EstadoFacturacion.EMITIDA_PENDIENTE_PAGO: return "EMITIDA_PENDIENTE_PAGO";
    case EstadoFacturacion.PAGADA: return "PAGADA";
    case EstadoFacturacion.PENDIENTE_PAGO: return "PENDIENTE_PAGO";
    default: return "SIN_FACTURA";
  }
};

const FacturacionMes = ({ open, onClose, actividades, masterClasses, pacientes, sanitarios }: FacturacionMesProps) => {
  const today = new Date();
  const history = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [actividadId, setActividadId] = useState<number | "">("");
  const [especialistaId, setEspecialistaId] = useState<number | "">("");
  const [estadoFacturacion, setEstadoFacturacion] = useState<EstadoFacturacionResponse>({});
  const [anchorClase, setAnchorClase] = useState<HTMLElement | null>(null);
  const [expandedClases, setExpandedClases] = useState<Record<string, boolean>>({});
  
  const [selectedMasterClassId, setSelectedMasterClassId] = useState<string | null>(null);
  const [loadingFacturarClase, setLoadingFacturarClase] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmResumen, setConfirmResumen] = useState<{
    total: number;
    sinConfiguracion: number;
    noFacturables: number;
    yaFacturados: number;
  } | null>(null);
  const [selectedEntidadId, setSelectedEntidadId] = useState<string | null | undefined>(undefined);
  const [errorEntidad, setErrorEntidad] = useState(false);

  const [openFacturarForm, setOpenFacturarForm] = useState(false);
  const [facturacionSeleccionada, setFacturacionSeleccionada] = useState<{
    pacienteId: string;
    actividadId: number;
    claseId: string;
    month: number;
    year: number;
    configuracion: IServiceClassInvoice;
  } | null>(null);

  const [openDetalleFactura, setOpenDetalleFactura] = useState(false);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState<{ factura: IFacturaCompleta, paciente: IPaciente, actividadId: number, periodo: string, motivoNoFacturable?: string, estado: EstadoFacturacionClase } | null>(null);

  const menuClaseOpen = Boolean(anchorClase);
  const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });

  useEffect(() => {
    getEstadoFacturacionMes();
  }, [month, year, actividadId, especialistaId]);

  useEffect(() => {
    setExpandedClases({});
  }, [month, year, actividadId, especialistaId]);

  const getEstadoFacturacionMes = async () => {
    try {
      setLoading(true);
      const response = await FacturacionService.getEstadoFacturacionMes({
        month,
        year,
        // "" = sin filtro; el 0 es un valor válido (el titular, la primera actividad de la clínica)
        actividadId: actividadId === "" ? undefined : actividadId,
        especialistaId: especialistaId === "" ? undefined : especialistaId,
      });
      setEstadoFacturacion(response.data.estados || {});
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "FacturacionMes.Component.getEstadoFacturacionMes",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleClase = (claseId: string) => {
    setExpandedClases(prev => ({
      ...prev,
      [claseId]: !prev[claseId]
    }));
  };

  const handleOpenMenuClase = (event: React.MouseEvent<HTMLElement>, masterClassId: string) => {
    event.stopPropagation();
    setSelectedMasterClassId(masterClassId);
    if (!event.currentTarget) return;
    setAnchorClase(event.currentTarget);
  };

  const getResumenClase = (masterClassId: string) => {
    const actividad = masterClasses.find(m => m._id === masterClassId);
    if (!actividad) return null;

    const actividadId = Number(actividad.title);

    // Una vez por paciente: puede tener varios tramos en el mes (baja y vuelta)
    const pacientes = actividad.pacientes.filter(p => {
      const ps = moment.utc(p.start);
      const pe = moment.utc(p.end);
      return ps.isSameOrBefore(monthEnd) && pe.isSameOrAfter(monthStart);
    }).filter((p, i, lista) => lista.findIndex(x => x.id === p.id) === i);

    const resumen = {
      facturables: [] as string[],
      noFacturables: [] as string[],
      sinConfiguracion: [] as string[],
      yaFacturados: [] as string[],
    };

    pacientes.forEach(p => {
      const estado = getEstado(p.id, String(actividadId));

      if (!estado) {
        return;
      }

      if (estado.estado === "SIN_FACTURA") {
        const cfg = estado.configuracion;
        if (cfg?.facturable === false) {
          resumen.noFacturables.push(p.id);
        } else {
          resumen.facturables.push(p.id);
        }
      } else if (estado.estado === "NO_FACTURABLE" || estado.estado === "NO_FACTURABLE_PUNTUAL") {
        resumen.noFacturables.push(p.id);
      } else {
        resumen.yaFacturados.push(p.id);
      }

      if (estado.estado === "SIN_CONFIGURACION") {
        resumen.sinConfiguracion.push(p.id);
      }
    });

    return resumen;
  };

  const handleFacturarClase = async () => {
    if (!selectedMasterClassId) return;

    const resumen = getResumenClase(selectedMasterClassId);
    if (!resumen) return;

    const totalFacturables = resumen.facturables.length;
    if (totalFacturables === 0) {
      let msg = "";

      if (resumen.sinConfiguracion.length && resumen.noFacturables.length) {
        msg = `No hay pacientes facturables. ${resumen.sinConfiguracion.length} sin configuración y ${resumen.noFacturables.length} no facturables.`;
      } else if (resumen.sinConfiguracion.length) {
        msg = `Falta configuración para ${resumen.sinConfiguracion.length} paciente(s).`;
      } else if (resumen.noFacturables.length && resumen.yaFacturados.length) {
        msg = `Todos los pacientes tiene factura creada o están configurados como no facturables.`;
      } else if (resumen.noFacturables.length) {
        msg = `Todos los pacientes están configurados como no facturables.`;
      } else if (resumen.yaFacturados.length) {
        msg = `Todos los pacientes tienen factura creada.`;
      } else {
        msg = "No hay pacientes facturables.";
      }

      setAlert({ open: true, severity: "info", msg });
      handleCloseMenuClase();
      return;
    }

    setConfirmResumen({
      total: totalFacturables,
      sinConfiguracion: resumen.sinConfiguracion.length,
      noFacturables: resumen.noFacturables.length,
      yaFacturados: resumen.yaFacturados.length,
    });

    setConfirmOpen(true);
  };

  const handleConfirmFacturar = async () => {
    if (!selectedMasterClassId || !confirmResumen) return;
    if (esFacturacionMultiEntidad() && selectedEntidadId === undefined) {
      setErrorEntidad(true);
      return;
    }
    setConfirmOpen(false);
    setLoadingFacturarClase(true);
    try {
      const response = await FacturacionService.createFacturasClase({
        userId: ConfigUtils.getUserId(),
        masterClassId: selectedMasterClassId,
        month,
        year,
        entityId: selectedEntidadId
      });
      setAlert({ open: true, severity: "success", msg: response.data.message });
      response.data.resultados?.forEach((r: any) => {
        const act = masterClasses.find(m => m._id === selectedMasterClassId)?.title;
        const key = `${r.pacienteId}_${act}_${periodo}`;

        setEstadoFacturacion(prev => ({
          ...prev,
          [key]: {
            ...prev[key],
            estado: "PENDIENTE_PAGO",
            factura: r.factura
          }
        }));
      });
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "FacturacionMes.Component.handleFacturarClase",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoadingFacturarClase(false);
      handleCloseMenuClase();
      setExpandedClases(prev => ({
        ...prev,
        [selectedMasterClassId]: true
      }));
      setSelectedMasterClassId(null);
    }
  }

  const handleCloseMenuClase = () => {
    setAnchorClase(null);
    setSelectedMasterClassId(null);
    setSelectedEntidadId(undefined);
    setErrorEntidad(false);
  };

  const periodo = `${year}-${month.toString().padStart(2, "0")}`;

  const getEstado = (pacienteId: string, actividadId?: string) =>
    actividadId
      ? estadoFacturacion[`${pacienteId}_${actividadId}_${periodo}`]
      : undefined;

  const isAccionBloqueada = (estado?: EstadoFacturacionClase) =>
    !estado || estado === "SIN_CONFIGURACION" || estado === "NO_FACTURABLE";

  const monthStart = useMemo(() => moment.utc([year, month - 1]), [month, year]);
  const monthEnd = useMemo(() => moment.utc([year, month - 1]).endOf("month"), [month, year]);

  const clasesPorDia = useMemo(() => {
    const dias: Record<number, IMasterClass[]> = {
      0: [], // Lunes
      1: [], // Martes
      2: [], // Miércoles
      3: [], // Jueves
      4: [], // Viernes
    };
  
    if (!masterClasses) return dias;
  
    masterClasses.forEach(c => {
      if (!c.start || !c.terminarRepetir) return;
    
      const start = moment.utc(c.start);
      const end = moment.utc(c.terminarRepetir);
      const activaEnMes = end.isSameOrAfter(monthStart) && start.isSameOrBefore(monthEnd);
    
      const actividadOk =
        actividadId === "" || c.title === String(actividadId);
      const especialistaOk =
        especialistaId === "" || String(c.especialistaId) === String(especialistaId);
    
      if (activaEnMes && actividadOk && especialistaOk) {
        const day = c.weekDay;
        if (day >= 0 && day <= 4) {
          dias[day].push(c);
        }
      }
    });
  
    Object.keys(dias).forEach(k => {
      dias[Number(k)].sort((a, b) => {
        const dateA = new Date(a.start);
        const dateB = new Date(b.start);
      
        const minutesA = dateA.getHours() * 60 + dateA.getMinutes();
        const minutesB = dateB.getHours() * 60 + dateB.getMinutes();
      
        return minutesA - minutesB;
      });
    });
  
    return dias;
  }, [masterClasses, monthStart, monthEnd, actividadId, especialistaId]);

  const sanitariosColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    sanitarios.forEach((s) => {
      map[String(s.internalID)] = s.color;
    });
    return map;
  }, [sanitarios]);

  const pacientesMap = useMemo(() => {
    const map: Record<string, IPaciente> = {};
    pacientes.forEach((p) => {
      if (p._id) map[p._id] = p;
    });
    return map;
  }, [pacientes]);

  const renderCard = (clase: IMasterClass) => {
    const isOpen = !!expandedClases[clase._id!];

    const start = new Date(clase.start);
    const end = new Date(start.getTime() + (clase.duracion || 0) * 60000);

    const hIni = start.getHours().toString().padStart(2, "0");
    const mIni = start.getMinutes().toString().padStart(2, "0");
    const hFin = end.getHours().toString().padStart(2, "0");
    const mFin = end.getMinutes().toString().padStart(2, "0");

    const actividad = actividades.find((a) => a.internalID?.toString() === clase.title);
    const capacidad = actividad?.capacity ?? 0;

    // Una vez por paciente: puede tener varios tramos en el mes (baja y vuelta)
    const pacientesActivos = clase.pacientes.filter((p) => {
      const ps = moment.utc(p.start).startOf("day");
      const pe = moment.utc(p.end).endOf("day");

      return ps.isSameOrBefore(monthEnd) && pe.isSameOrAfter(monthStart);
    }).filter((p, i, lista) => lista.findIndex(x => x.id === p.id) === i);

    const ocupados = pacientesActivos.length;
    const hasPacientes = ocupados > 0;
    const libres = capacidad - ocupados;
    const especialistaColor = sanitariosColorMap[String(clase.especialistaId)];

    const pacientesOrdenados = [...pacientesActivos].sort((a, b) => {
      const estadoA = getEstado(a.id, actividad?.internalID?.toString())?.estado as EstadoFacturacionClase;
      const estadoB = getEstado(b.id, actividad?.internalID?.toString())?.estado as EstadoFacturacionClase;

      const prioridadA = estadoPrioridad[estadoA] ?? 999;
      const prioridadB = estadoPrioridad[estadoB] ?? 999;

      return prioridadA - prioridadB;
    });

    const handleOpenFacturarForm = (pacienteId: string, actividadId: number, claseId: string, configuracion: IServiceClassInvoice) => {
      setFacturacionSeleccionada({
        pacienteId,
        actividadId,
        claseId,
        month,
        year,
        configuracion
      });
      setOpenFacturarForm(true);
    };

    return (
      <div
        key={clase._id}
        style={{
          border: "1px solid #e0e0e0",
          borderRadius: 10,
          padding: "10px",
          background: "#fff",
          borderLeft: especialistaColor
          ? `4px solid ${especialistaColor}`
          : "1px solid #e0e0e0",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {hasPacientes && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleClase(clase._id!);
                }}
                sx={{
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                  padding: 0.5,
                  width: 22,
                  height: 22,
                }}
              >
                <ExpandMoreIcon fontSize="small" sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {actividad?.title || "Clase"}{" "}
              <span style={{ color: "#666" }}>
                {`${hIni}:${mIni} - ${hFin}:${mFin}`}
              </span>
            </div>
          </div>
            
          {hasPacientes && (
            <Tooltip title="Opciones de clase">
              <IconButton
                size="small"
                onClick={(e) => handleOpenMenuClase(e, clase._id!)}
                sx={{
                  padding: 0.5,
                  width: 22,
                  height: 22,
                }}
              >
                <MoreVertIcon fontSize="small" sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </div>

        <div style={{ marginTop: 4, fontSize: 11 }}>
          Plazas: {ocupados} / {capacidad}
          {libres > 0 ? (
            <span style={{ color: "green" }}> (libres: {libres})</span>
          ) : (
            <span style={{ color: "red" }}> (completo)</span>
          )}
        </div>

        {isOpen && <Stack spacing={1} sx={{ marginTop: 1 }}>
          {pacientesOrdenados.map((p) => {
            const paciente = pacientesMap[p.id];
            if (!paciente) return null;

            const estado = getEstado(p.id, actividad?.internalID?.toString());
            const info = estado?.estado!;
            const bloqueado = isAccionBloqueada(info);

            return (
              <div
                key={p.id}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  padding: "4px 6px",
                  background: "#fafafa",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ fontSize: 12, fontWeight: info ? 600 : 400 }}>
                  {paciente.nombre} {paciente.apellidos}
                </div>

                {estado && (
                  <Tooltip title={estadoTooltip[info] || "Estado"} placement="top">
                    <span>
                      <IconButton 
                        size="small" 
                        disabled={bloqueado} 
                        onClick={() => {
                          const estado = getEstado(p.id, actividad?.internalID?.toString());

                          if (estado?.estado === "SIN_FACTURA") {
                              handleOpenFacturarForm(
                              p.id,
                              Number(actividad?.internalID),
                              clase._id!,
                              estado?.configuracion!
                            );
                          } else {
                            setFacturaSeleccionada({ 
                              factura: estado?.factura!, 
                              paciente: pacientes.find(p => p._id === estado?.configuracion.pacienteId)!, 
                              actividadId: estado?.configuracion.actividadId!, 
                              periodo,
                              motivoNoFacturable: estado?.motivo!,
                              estado: estado?.estado! 
                            });
                            setOpenDetalleFactura(true);
                          }
                        }}
                      >
                        {info === "PAGADA" || info === "PENDIENTE_PAGO" ? (
                          <Badge
                            variant="dot"
                            color="warning"
                            overlap="circular"
                            anchorOrigin={{ vertical: "top", horizontal: "right" }}
                          >
                            <EuroIcon color={estadoColorIcon[info] || "inherit"} />
                          </Badge>
                        ) : info === "SIN_CONFIGURACION" ? (
                          <Badge
                            variant="dot"
                            color="error"
                            overlap="circular"
                            anchorOrigin={{ vertical: "top", horizontal: "right" }}
                          >
                            <EuroIcon color={estadoColorIcon[info] || "inherit"} />
                          </Badge>
                        ) : info === "ANULADA" ? (
                          <Badge
                            variant="dot"
                            overlap="circular"
                            anchorOrigin={{ vertical: "top", horizontal: "right" }}
                          >
                            <BlockIcon color={estadoColorIcon[info] || "inherit"} />
                          </Badge>
                        ) : (
                          <EuroIcon color={estadoColorIcon[info] || "inherit"} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </div>
            );
          })}
        </Stack>}
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={loadingFacturarClase ? undefined : onClose} maxWidth="lg" fullWidth>
        <Backdrop open={loadingFacturarClase} sx={{ position: "absolute", zIndex: 10 }}>
          <CircularProgress color="inherit" />
        </Backdrop>

        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 20,
            fontWeight: 600,
          }}
        >
          <span>
            Facturación {constantes.monthNames[month - 1]} {year}
          </span>

          <IconButton onClick={onClose} size="small">
            <ClearIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Grid
          container
          spacing={2}
          sx={{
            px: 3,
            pb: 2,
            alignItems: "center",
          }}
        >
          {/* Mes */}
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: 13 }}>Mes</InputLabel>
              <Select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                sx={{ "& .MuiSelect-select": { fontSize: 13 } }}
                label="Mes"
              >
                {constantes.monthNames.map((m, i) => (
                  <MenuItem key={i} value={i + 1}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
              
          {/* Año */}
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: 13 }}>Año</InputLabel>
              <Select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                sx={{ "& .MuiSelect-select": { fontSize: 13 } }}
                label="Año"
              >
                {Array.from({ length: 5 }, (_, i) => today.getFullYear() - 2 + i).map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
              
          {/* Actividad */}
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: 13 }}>Actividad</InputLabel>
              <Select
                value={actividadId}
                onChange={(e) => setActividadId(Number(e.target.value))}
                sx={{ "& .MuiSelect-select": { fontSize: 13 } }}
                label="Actividad"
                startAdornment={
                  actividadId === "" ? null : (
                    <IconButton size="small" onClick={() => setActividadId("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )
                }
              >
                {actividades.map((a) => (
                  <MenuItem key={a.internalID} value={a.internalID!}>
                    {a.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
              
          {/* Profesor */}
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: 13 }}>Profesor</InputLabel>
              <Select
                value={especialistaId}
                onChange={(e) => setEspecialistaId(Number(e.target.value))}
                sx={{ "& .MuiSelect-select": { fontSize: 13 } }}
                label="Profesor"
                startAdornment={
                  especialistaId === "" ? null : (
                    <IconButton size="small" onClick={() => setEspecialistaId("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )
                }
              >
                {sanitarios
                  .filter((s) => s.activo && s.role !== constantes.ROLES.SECRETARIO)
                  .map((a) => (
                    <MenuItem key={a.internalID} value={a.internalID!}>
                      {a.firstName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {loading ? (
          <CircularProgress style={{ margin: "20px auto" }} />
        ) : (
          <div style={{ padding: "12px" }}>
            {isDesktop ? (
              (() => {
                const diasConClases = Object.entries(clasesPorDia).filter(
                  ([, lista]) => lista.length > 0
                );
              
                return (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${diasConClases.length || 1}, 1fr)`,
                      gap: "12px",
                    }}
                  >
                    {diasConClases.length === 0 ? (
                      <div style={{ color: "#999", fontSize: 13 }}>Sin clases este mes</div>
                    ) : (
                      diasConClases.map(([dia, lista]) => (
                        <div key={dia}>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              marginBottom: 8,
                              color: "#444",
                            }}
                          >
                            {["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"][Number(dia)]}
                          </div>
                          
                          <div style={{ display: "grid", gap: "12px" }}>
                            {lista.map(renderCard)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                );
              })()
            ) : (
              Object.entries(clasesPorDia).map(([dia, lista]) => (
                <div key={dia} style={{ marginBottom: 18 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 8,
                      color: "#444",
                    }}
                  >
                    {["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"][Number(dia)]}
                  </div>
                  
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                      gap: "12px",
                    }}
                  >
                    {lista.length === 0 ? (
                      <div style={{ color: "#999", fontSize: 13 }}>Sin clases</div>
                    ) : (
                      lista.map(renderCard)
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <Menu
          anchorEl={anchorClase}
          open={menuClaseOpen}
          onClose={handleCloseMenuClase}
          PaperProps={{ sx: { minWidth: 150 } }}
          disablePortal
        >
          <MenuItem onClick={handleFacturarClase}>{loadingFacturarClase ? "Facturando..." : "Facturar clase"}</MenuItem>
        </Menu>
        
      </Dialog>

      {/* CONFIRMACION FACTURAR CLASE */}
      <Dialog open={confirmOpen} onClose={() => {setConfirmOpen(false); handleCloseMenuClase()}} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, pb: 0 }}>Confirmar facturación</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmResumen?.total === 1
                ? <>Se va a crear <strong>1</strong> factura. ¿Quieres continuar?</>
                : <>Se van a crear <strong>{confirmResumen?.total}</strong> facturas. ¿Quieres continuar?</>}
            </DialogContentText>
            <SelectorEntidadFacturacion
              value={selectedEntidadId}
              onChange={(v) => { setSelectedEntidadId(v); setErrorEntidad(false); }}
              error={errorEntidad}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={() => {setConfirmOpen(false); handleCloseMenuClase()}}>
              NO
            </Button>
            <Button onClick={handleConfirmFacturar}>
              SI
            </Button>
            </DialogActions>
      </Dialog>

      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
          {alert.msg}
        </Alert>
      </Snackbar>

      {openFacturarForm && 
      <FacturaClaseForm
        open={openFacturarForm}
        onClose={() => {
          setOpenFacturarForm(false);
          setFacturacionSeleccionada(null);
        }}
        paciente={pacientes.find(p => p._id === facturacionSeleccionada?.pacienteId)!}
        data={facturacionSeleccionada!}
        onSuccess={(estado, factura, motivo) => {
          const sel = facturacionSeleccionada;
          if (!sel) return;

          const periodo = `${sel.year}-${sel.month.toString().padStart(2, "0")}`;
          const key = `${sel.pacienteId}_${sel.actividadId}_${periodo}`;
              
          setEstadoFacturacion(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              estado: estado,
              factura: factura,
              ...(motivo !== undefined ? { motivo } : {})
            },
          }));
          if (estado !== "NO_FACTURABLE_PUNTUAL") {
            setFacturaSeleccionada({ factura: factura!, paciente: pacientes.find(p => p._id === sel.pacienteId)!, actividadId: sel.actividadId, periodo, estado, });
            setOpenDetalleFactura(true);
          }
        }}
      />}

      {openDetalleFactura && facturaSeleccionada &&
      <DetalleFacturaClase
        key={facturaSeleccionada.factura?._id}
        open={openDetalleFactura}
        onClose={() => { setOpenDetalleFactura(false) }}
        actividadId={facturaSeleccionada.actividadId!}
        facturaId={facturaSeleccionada.factura?._id!}
        factura={facturaSeleccionada.factura!}
        paciente={facturaSeleccionada.paciente!}
        periodo={facturaSeleccionada.periodo!}
        motivoNoFacturable={facturaSeleccionada.motivoNoFacturable}
        facturable={facturaSeleccionada.estado === "NO_FACTURABLE_PUNTUAL" ? false : true}
        onEdit={(pacienteId, actividadId, factura, estado, motivo, facturaPeriodo) => {
          const key = `${pacienteId}_${actividadId}_${facturaPeriodo ?? periodo}`;

          setEstadoFacturacion(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              estado: estado,
              factura: factura,
              ...(motivo !== undefined ? { motivo } : {})
            },
          }));
        }}
        onFacturaPagada={(pacienteId, actividadId, _id, _fechaPago, estado, estadoPago, facturaPeriodo) => {
          const key = `${pacienteId}_${actividadId}_${facturaPeriodo ?? periodo}`;
          setEstadoFacturacion(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              estado: getEstadoFacturacionClase(estado, estadoPago),
            },
          }));
          setAlert({ open: true, severity: "success", msg: "Factura pagada correctamente" });
        }}
        onFacturaEmitida={(pacienteId, actividadId, _id, estado, estadoPago, facturaPeriodo) => {
          const key = `${pacienteId}_${actividadId}_${facturaPeriodo ?? periodo}`;
          setEstadoFacturacion(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              estado: getEstadoFacturacionClase(estado, estadoPago),
            },
          }));
          setAlert({ open: true, severity: "success", msg: "Factura emitida correctamente" });
        }}
        onUpdateMotivo={(pacienteId, actividadId, motivo, facturaPeriodo) => {
          const key = `${pacienteId}_${actividadId}_${facturaPeriodo ?? periodo}`;
          setEstadoFacturacion(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              motivo,
            },
          }));
        }}
        onFacturar={(pacienteId, actividadId, periodo) => {
          const estado = estadoFacturacion[`${pacienteId}_${actividadId}_${periodo}`]
          setFacturacionSeleccionada({
            pacienteId,
            actividadId,
            claseId: "",
            month: Number(periodo.split("-")[1]),
            year: Number(periodo.split("-")[0]),
            configuracion: estado.configuracion
          })
          setOpenFacturarForm(true)
        }}
        onFacturaRectificada={(pacienteId, actividadId, facturaNueva, facturaPeriodo) => {
          const key = `${pacienteId}_${actividadId}_${facturaPeriodo ?? periodo}`;

          setEstadoFacturacion(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              factura: facturaNueva,
              estado: getEstadoFacturacionClase(facturaNueva.estado, facturaNueva.estadoPago)
            },
          }));
          setAlert({ open: true, severity: "success", msg: "Factura rectificada correctamente" });
        }}
        onAnularFactura={(pacienteId, actividadId, periodo, id, motivo) => {
          const key = `${pacienteId}_${actividadId}_${periodo}`;
          setEstadoFacturacion(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              factura: {
                ...prev[key]?.factura,
                estado: "Anulada"
              } as IFacturaCompleta,
              estado: "ANULADA"
            },
          }));
        }}
        onEntidadCambiada={(pacienteId, actividadId, periodo, _facturaAnuladaId, facturaNueva) => {
          // El registro del mes pasa a apuntar a la factura nueva (Borrador en la entidad de destino).
          const key = `${pacienteId}_${actividadId}_${periodo}`;
          setEstadoFacturacion(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              factura: facturaNueva,
              estado: getEstadoFacturacionClase(facturaNueva.estado, facturaNueva.estadoPago)
            },
          }));
        }}
        onEstadoCambiado={(pacienteId, actividadId, periodo, factura) => {
          // La factura había cambiado en otra pantalla: la celda del mes pasa a reflejar su estado actual.
          const key = `${pacienteId}_${actividadId}_${periodo}`;
          setEstadoFacturacion(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              factura,
              estado: getEstadoFacturacionClase(factura.estado, factura.estadoPago)
            },
          }));
        }}
        onFacturaEliminada={(pacienteId, actividadId, periodo, _facturaEliminadaId, aviso) => {
          // El mes queda sin factura (se puede volver a facturar), salvo que se haya eliminado una rectificativa: entonces
          // vuelve a ser la vigente la factura original.
          const key = `${pacienteId}_${actividadId}_${periodo}`;
          const vigente = aviso.facturaVigente;
          setEstadoFacturacion(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              estado: vigente ? getEstadoFacturacionClase(vigente.estado, vigente.estadoPago) : "SIN_FACTURA",
              factura: vigente
            },
          }));
          setAlert({ open: true, severity: "success", msg: "Factura eliminada correctamente" });
        }}
      />
      }
    </>
  );
};

export default FacturacionMes;